import { useCallback, useRef } from 'react';
import type { FrequencyData } from '../../../services/AudioManager';
import { GEMINI_TTS_SAMPLE_RATE, PCMStreamPlayer } from '../audio/pcmStreamPlayer';

const DRAIN_TIMEOUT_MS = 10_000;

export type GeminiTtsStreamHandlers = {
  onStreamStart?: () => void;
  onStreamDone?: () => void;
  onStreamError?: (message: string) => void;
};

function decodeBase64Pcm(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export function useGeminiTtsStream(handlers: GeminiTtsStreamHandlers = {}) {
  const audioContextRef = useRef<AudioContext | null>(null);
  const playerRef = useRef<PCMStreamPlayer | null>(null);
  const activeStreamIdRef = useRef<string | null>(null);
  const streamDoneRef = useRef(false);
  const allChunksQueuedRef = useRef(false);
  const handlersRef = useRef(handlers);
  const chunkChainRef = useRef(Promise.resolve());
  const finishDispatchedRef = useRef(false);

  handlersRef.current = handlers;

  const waitForPlaybackDrain = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      const startedAt = Date.now();
      const poll = () => {
        if (!playerRef.current?.hasPendingAudio()) {
          resolve();
          return;
        }
        if (Date.now() - startedAt >= DRAIN_TIMEOUT_MS) {
          resolve();
          return;
        }
        window.setTimeout(poll, 50);
      };
      poll();
    });
  }, []);

  const attemptStreamFinish = useCallback(() => {
    if (finishDispatchedRef.current || !streamDoneRef.current || !allChunksQueuedRef.current) {
      return;
    }
    if (!playerRef.current || playerRef.current.hasPendingAudio()) {
      return;
    }
    finishDispatchedRef.current = true;
    handlersRef.current.onStreamDone?.();
    activeStreamIdRef.current = null;
    streamDoneRef.current = false;
    allChunksQueuedRef.current = false;
  }, []);

  const scheduleStreamFinish = useCallback(() => {
    void chunkChainRef.current.then(async () => {
      if (!streamDoneRef.current || !allChunksQueuedRef.current) {
        return;
      }
      await waitForPlaybackDrain();
      attemptStreamFinish();
    });
  }, [attemptStreamFinish, waitForPlaybackDrain]);

  const ensureAudio = useCallback(async () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext({ sampleRate: GEMINI_TTS_SAMPLE_RATE });
      playerRef.current = new PCMStreamPlayer(audioContextRef.current, GEMINI_TTS_SAMPLE_RATE);
      playerRef.current.onDrain(() => {
        scheduleStreamFinish();
      });
    }
    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }
    return playerRef.current!;
  }, [scheduleStreamFinish]);

  const resetStream = useCallback(() => {
    chunkChainRef.current = Promise.resolve();
    playerRef.current?.stop();
    activeStreamIdRef.current = null;
    streamDoneRef.current = false;
    allChunksQueuedRef.current = false;
    finishDispatchedRef.current = false;
  }, []);

  const handleStreamStart = useCallback(async (streamId: string) => {
    if (activeStreamIdRef.current === streamId && playerRef.current) {
      handlersRef.current.onStreamStart?.();
      return;
    }
    resetStream();
    activeStreamIdRef.current = streamId;
    streamDoneRef.current = false;
    allChunksQueuedRef.current = false;
    finishDispatchedRef.current = false;
    await ensureAudio();
    handlersRef.current.onStreamStart?.();
  }, [ensureAudio, resetStream]);

  const handleStreamChunk = useCallback(async (streamId: string, data?: string | null) => {
    if (!data || activeStreamIdRef.current !== streamId) {
      return;
    }
    chunkChainRef.current = chunkChainRef.current.then(async () => {
      const player = await ensureAudio();
      player.queue(decodeBase64Pcm(data));
    });
    await chunkChainRef.current;
  }, [ensureAudio]);

  const handleStreamDone = useCallback((streamId: string) => {
    if (activeStreamIdRef.current !== streamId) {
      return;
    }
    streamDoneRef.current = true;
    chunkChainRef.current = chunkChainRef.current.then(() => {
      allChunksQueuedRef.current = true;
    });
    scheduleStreamFinish();
  }, [scheduleStreamFinish]);

  const handleStreamError = useCallback((streamId: string, message?: string) => {
    if (activeStreamIdRef.current && activeStreamIdRef.current !== streamId) {
      return;
    }
    resetStream();
    handlersRef.current.onStreamError?.(message || 'TTS stream failed');
  }, [resetStream]);

  const cleanup = useCallback(() => {
    resetStream();
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    playerRef.current = null;
  }, [resetStream]);

  const getStreamFrequencyData = useCallback((): FrequencyData | null => {
    if (!playerRef.current) {
      return null;
    }
    return playerRef.current.getFrequencyData();
  }, []);

  return {
    handleStreamStart,
    handleStreamChunk,
    handleStreamDone,
    handleStreamError,
    cleanup,
    resetStream,
    getStreamFrequencyData,
  };
}
